<template>
  <V2ConversationConfirm
    v-if="message.requiresInput && message.inputType !== 'text' && isLast"
    :options="message.options"
    :title="message.result"
    :type="message.inputType"
    @action="emit('confirm', $event)"
  />
  <div
    v-else
    ref="wrapper"
    class="p-2 pb-0 px-4 rounded-xl max-w-[80%] relative"
    :class="{
      'bg-primary-600 dark:bg-[#2576a2] text-white mr-auto': sender === 'assistant',
      'bg-white dark:bg-slate-600 ml-auto': sender === 'user' && !isWidget,
      'bg-surface-100 dark:bg-surface-600 ml-auto': sender === 'user' && !!isWidget,
      hidden: !message.result,
    }"
  >
    <slot>
      <MarkdownText :text="message.result" />
    </slot>
    <p
      v-if="date"
      class="text-xs opacity-70 -mt-2 pb-1 font-medium"
      :class="sender === 'user' ? 'text-right' : ''"
    >
      {{ date }}
    </p>
  </div>
</template>

<script setup lang="ts">
import MarkdownText from "@/components/shared/MarkdownText.vue";
import type { V2ParsedMessageContent } from "@/types/v2";
import V2ConversationConfirm from "./V2ConversationConfirm.vue";

const props = defineProps<{
  message: V2ParsedMessageContent;
  created?: string;
  sender: "assistant" | "user";
  isWidget?: boolean;
  isLast?: boolean;
}>();

const emit = defineEmits<{
  ready: [];
  confirm: [string];
}>();
const wrapper = ref<HTMLElement | null>(null);
const router = useRouter();
const linkHandlers = new Map<HTMLAnchorElement, (e: Event) => void>();
const date = formatChatDate(props.created || "");

onMounted(() => {
  nextTick(() => {
    if (props.isLast) emit("ready");
    const links = wrapper.value?.querySelectorAll("a");
    if (!links) return;

    links.forEach(link => {
      const href = link.getAttribute("href");
      if (!href) return;

      try {
        const url = new URL(href, window.location.origin);
        const internal =
          !url.pathname || url.pathname === "/" ? null : router.resolve(url.pathname);

        if (internal && internal.name !== RouteNames.NOT_FOUND) {
          // Remove target="_blank" for internal links
          link.removeAttribute("target");
          link.removeAttribute("rel");

          // Create and store click handler
          const clickHandler = (e: Event) => {
            e.preventDefault();
            router.push(internal);
          };

          linkHandlers.set(link, clickHandler);
          link.addEventListener("click", clickHandler);
        }
      } catch (error) {
        // If URL parsing fails, leave the link as is
        console.warn("Failed to parse URL:", href, error);
      }
    });
  });
});

onBeforeUnmount(() => {
  // Clean up event listeners
  linkHandlers.forEach((handler, link) => {
    link.removeEventListener("click", handler);
  });
  linkHandlers.clear();
});
</script>
